/**
 * 格式校验工具方法集合
 * 包含手机号、邮箱、身份证、URL、IP、经纬度等常用格式校验
 */
export default {
  /**
   * 校验是否为合法手机号（以1开头的11位数字）
   * @param {string} value - 待校验的字符串
   * @returns {boolean} 是否为合法手机号
   */
  isPhone(value) {
    const reg = /^1\d{10}$/;
    return reg.test(value);
  },

  /**
   * 校验是否为固定电话（支持400/800号码、区号-号码格式）
   * @param {string} value - 待校验的字符串
   * @returns {boolean} 是否为合法固话号码
   */
  isTel(value) {
    const reg = /^(400|800)([0-9\-]{7,10})|(([0-9]{4}|[0-9]{3})(-| )?)?([0-9]{7,8})((-| |转)*([0-9]{1,4}))?$/;
    return reg.test(value);
  },

  /**
   * 校验是否为合法邮箱地址
   * @param {string} value - 待校验的字符串
   * @returns {boolean} 是否为合法邮箱
   */
  isEmail(value) {
    const reg = /^([a-zA-Z0-9_.-])+@(([a-zA-Z0-9-])+\.)+([a-zA-Z0-9]{2,4})+$/;
    return reg.test(value);
  },

  /**
   * 校验是否为合法网址（支持 http/https，也支持 # 开头的锚点）
   * @param {string} value - 待校验的字符串
   * @returns {boolean} 是否为合法网址
   */
  isUrl(value) {
    const reg = /(^#)|(^http(s*):\/\/[^\s]+\.[^\s]+)/;
    return reg.test(value);
  },

  /**
   * 校验是否为数字（包括整数、浮点数、负数）
   * @param {*} value - 待校验的值
   * @returns {boolean} 是否为数字
   */
  isNumber(value) {
    return !isNaN(value);
  },

  /**
   * 校验是否为合法日期格式（支持 yyyy-MM-dd 或 yyyy/MM/dd）
   * @param {string} value - 待校验的字符串
   * @returns {boolean} 是否为合法日期
   */
  isDate(value) {
    const reg = /^(\d{4})[-/](\d{1}|0\d{1}|1[0-2])([-/](\d{1}|0\d{1}|[1-2][0-9]|3[0-1]))*$/;
    return reg.test(value);
  },

  /**
   * 校验是否为身份证号码（基础校验，仅检查位数和格式）
   * @param {string} value - 待校验的字符串
   * @returns {boolean} 是否符合身份证基本格式
   */
  isIdentity(value) {
    const reg = /(^\d{15}$)|(^\d{17}(x|X|\d)$)/;
    return reg.test(value);
  },

  /**
   * 校验是否为整数（含正负整数）
   * @param {string|number} value - 待校验的值
   * @returns {boolean} 是否为整数
   */
  isDigits(value) {
    const reg = /^-?\d+$/;
    return reg.test(value);
  },

  /**
   * 校验是否为正整数（不含0）
   * @param {string|number} value - 待校验的值
   * @returns {boolean} 是否为正整数
   */
  isDigitsP(value) {
    const reg = /^[1-9]\d*$/;
    return reg.test(value);
  },

  /**
   * 校验是否为负整数（不含0）
   * @param {string|number} value - 待校验的值
   * @returns {boolean} 是否为负整数
   */
  isDigitsN(value) {
    const reg = /^-[1-9]\d*$/;
    return reg.test(value);
  },

  /**
   * 校验是否为非负整数（正整数或0）
   * @param {string|number} value - 待校验的值
   * @returns {boolean} 是否为非负整数
   */
  isDigitsPZ(value) {
    const reg = /^\d+$/;
    return reg.test(value);
  },

  /**
   * 校验是否为非正整数（负整数或0）
   * @param {string|number} value - 待校验的值
   * @returns {boolean} 是否为非正整数
   */
  isDigitsNZ(value) {
    const reg = /^-[1-9]\d*|0/;
    return reg.test(value);
  },

  /**
   * 校验字符串长度是否在指定范围内
   * @param {*} value - 待校验的值
   * @param {number} [minLength] - 最小长度
   * @param {number} [maxLength] - 最大长度
   * @returns {boolean} 长度是否符合要求
   */
  maxMinLength(value, minLength, maxLength) {
    if (value === undefined || value === null) return !minLength;
    if (minLength && value.toString().length < minLength) return false;
    return !(maxLength !== undefined && maxLength !== null && value.toString().length > maxLength);
  },

  /**
   * 校验数值是否在指定范围内
   * @param {number} value - 待校验的数值
   * @param {number} [min] - 最小值
   * @param {number} [max] - 最大值
   * @returns {boolean} 数值是否符合要求
   */
  maxMin(value, min, max) {
    if (value === undefined || value === null) return min === undefined || min === null;
    if (min !== undefined && min !== null && value < min) return false;
    return !(max !== undefined && max !== null && value > max);
  },

  /**
   * 校验是否为中文姓名（2-4个中文字符）
   * @param {string} value - 待校验的字符串
   * @returns {boolean} 是否为合法中文姓名
   */
  isChina(value) {
    const reg = /^[\u4E00-\u9FA5]{2,4}$/;
    return reg.test(value);
  },

  /**
   * 校验是否为合法端口号（0-65535）
   * @param {string|number} value - 待校验的值
   * @returns {boolean} 是否为合法端口号
   */
  isPort(value) {
    const reg = /^([0-9]|[1-9]\d|[1-9]\d{2}|[1-9]\d{3}|[1-5]\d{4}|6[0-4]\d{3}|65[0-4]\d{2}|655[0-2]\d|6553[0-5])$/;
    return reg.test(value);
  },

  /**
   * 校验是否为合法IPv4地址
   * @param {string} value - 待校验的字符串
   * @returns {boolean} 是否为合法IP地址
   */
  isIP(value) {
    const reg = /^(\d{1,2}|1\d\d|2[0-4]\d|25[0-5])\.(\d{1,2}|1\d\d|2[0-4]\d|25[0-5])\.(\d{1,2}|1\d\d|2[0-4]\d|25[0-5])\.(\d{1,2}|1\d\d|2[0-4]\d|25[0-5])$/;
    return reg.test(value);
  },

  /**
   * 校验是否为合法经度（-180.0 ~ +180.0，整数部分0~180，必须1-5位小数）
   * @param {string} value - 待校验的字符串
   * @returns {boolean} 是否为合法经度
   */
  isLongitude(value) {
    const reg = /^[-|+]?(0?\d{1,2}\.\d{1,5}|1[0-7]?\d{1}\.\d{1,5}|180\.0{1,5})$/;
    return reg.test(value);
  },

  /**
   * 校验是否为合法纬度（-90.0 ~ +90.0，整数部分0~90，必须1-5位小数）
   * @param {string} value - 待校验的字符串
   * @returns {boolean} 是否为合法纬度
   */
  isLatitude(value) {
    const reg = /^[-|+]?([0-8]?\d{1}\.\d{1,5}|90\.0{1,5})$/;
    return reg.test(value);
  },

  /**
   * 身份证号码强校验 - 校验格式、出生日期有效性、地区编码、末位校验码
   * @param {string} value - 15位或18位身份证号码
   * @returns {string|undefined} 校验失败返回错误描述字符串，校验通过返回 undefined
   */
  isIdentityStrong(value) {
    // 基础格式校验
    if (!this.isIdentity(value)) return '身份证号码格式错误';
    // 统一转为18位处理（15位补充年份19xx）
    const ai = value.length === 18 ? value.substring(0, 17) : value.substring(0, 6) + '19' + value.substring(6, 15);
    // 验证出生年月日是否合法
    const year = ai.substring(6, 10);
    const birthday = year + '/' + ai.substring(10, 12) + '/' + ai.substring(12, 14);
    if (!this.isDate(birthday)) return '身份证号码出生日期无效';
    // 验证出生日期是否在合理范围内（不超过150年，不早于当前时间）
    const now = new Date();
    if (now.getFullYear() - parseInt(year) > 150 || now.getTime() - new Date(birthday).getTime() < 0) return '身份证号码出生日期不在有效范围';
    // 验证省级地区编码（前两位）
    const areaCodes = [
      '11',
      '12',
      '13',
      '14',
      '15',
      '21',
      '22',
      '23',
      '31',
      '32',
      '33',
      '34',
      '35',
      '36',
      '37',
      '41',
      '42',
      '43',
      '44',
      '45',
      '46',
      '50',
      '51',
      '52',
      '53',
      '54',
      '61',
      '62',
      '63',
      '64',
      '65',
      '71',
      '81',
      '82',
      '91',
    ];
    if (areaCodes.indexOf(ai.substring(0, 2)) === -1) return '身份证号码地区编码错误';
    // 18位身份证验证末位校验码
    if (value.length === 18) {
      const valCode = ['1', '0', 'X', '9', '8', '7', '6', '5', '4', '3', '2'];
      const wi = [7, 9, 10, 5, 8, 4, 2, 1, 6, 3, 7, 9, 10, 5, 8, 4, 2];
      let totalMulAiWi = 0;
      for (let i = 0; i < 17; i++) totalMulAiWi += parseInt(ai.charAt(i)) * wi[i];
      if (value !== ai + valCode[totalMulAiWi % 11]) return '身份证号码最后一位错误';
    }
    // 校验通过返回 undefined
    return undefined;
  },
};
